/**
 * Dashboard registry - single source of truth for the dashboard's content and sidebar.
 *
 * - PROJECTS: sample projects only. They render inside the dashboard body.
 * - TOOL_LINKS: standalone top-level sidebar entries that
 *   route to existing app routes — Requirements Lifecycle, Requirement Doctor.
 * - KNOWLEDGE_BASES: global, own section + own pages.
 */

export type DashboardProject = {
  /** URL slug -> /dashboard/:id */
  id: string;
  name:
    | 'dashboard.projectNames.sampleProject1'
    | 'dashboard.projectNames.sampleProject2';
  description:
    | 'dashboard.projectDescriptions.sampleProject1'
    | 'dashboard.projectDescriptions.sampleProject2';
  role: 'owner' | 'maintainer' | 'collaborator' | 'viewer';
  knowledgeBases: number;
  members: number;
  testCases: number;
};

/** Sample projects only — these render inside the dashboard body. */
export const PROJECTS: DashboardProject[] = [
  {
    id: 'sample-project-1',
    name: 'dashboard.projectNames.sampleProject1',
    description: 'dashboard.projectDescriptions.sampleProject1',
    role: 'owner',
    knowledgeBases: 1,
    members: 2,
    testCases: 1,
  },
  {
    id: 'sample-project-2',
    name: 'dashboard.projectNames.sampleProject2',
    description: 'dashboard.projectDescriptions.sampleProject2',
    role: 'owner',
    knowledgeBases: 2,
    members: 3,
    testCases: 5,
  },
];

/**
 * Top-level tool links. Standalone sidebar entries — NOT
 * projects — that route to existing app routes. `short` is used by the
 * collapsed rail; the icon is resolved in the sidebar.
 */
export type ToolLink = {
  id: string;
  name: string;
  path: string;
  short: string;
};

export const TOOL_LINKS: ToolLink[] = [
  {
    id: 'requirements-lifecycle',
    name: 'Requirements Lifecycle',
    path: '/requirements',
    short: 'RL',
  },
  {
    id: 'requirement-doctor',
    name: 'Requirement Doctor',
    path: '/requirement-doctor',
    short: 'RD',
  },
];

/** Global knowledge bases (shared across projects). */
export type KnowledgeBaseDoc = {
  id: string;
  name: string;
  type: string;
  size: string;
  updated: string;
};

export type KnowledgeBase = {
  id: string;
  name:
    | 'dashboard.knowledgeBaseNames.apiDocumentation'
    | 'dashboard.knowledgeBaseNames.userRequirements';
  description:
    | 'dashboard.knowledgeBaseDescriptions.apiDocumentation'
    | 'dashboard.knowledgeBaseDescriptions.userRequirements';
  documents: KnowledgeBaseDoc[];
};

export const KNOWLEDGE_BASES: KnowledgeBase[] = [
  {
    id: 'api-documentation',
    name: 'dashboard.knowledgeBaseNames.apiDocumentation',
    description: 'dashboard.knowledgeBaseDescriptions.apiDocumentation',
    documents: [
      {
        id: 'd1',
        name: 'Auth API.pdf',
        type: 'PDF',
        size: '1.2 MB',
        updated: '2 days ago',
      },
      {
        id: 'd2',
        name: 'Orders API.md',
        type: 'Markdown',
        size: '18 KB',
        updated: '5 days ago',
      },
    ],
  },
  {
    id: 'user-requirements',
    name: 'dashboard.knowledgeBaseNames.userRequirements',
    description: 'dashboard.knowledgeBaseDescriptions.userRequirements',
    documents: [
      {
        id: 'd3',
        name: 'Checkout Flow.docx',
        type: 'Word',
        size: '340 KB',
        updated: '1 week ago',
      },
    ],
  },
];

export const DEFAULT_PROJECT_ID = PROJECTS[0].id;

export const getProject = (id?: string): DashboardProject =>
  PROJECTS.find(p => p.id === id) ?? PROJECTS[0];

export const getKnowledgeBase = (id?: string): KnowledgeBase | undefined =>
  KNOWLEDGE_BASES.find(kb => kb.id === id);
